import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Typography,
  Paper,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useBackdrop } from '../../common/components/backDrop_context.component';
import { notify } from '../../common/functions/react-toastify.function';
import { createFiberPop } from './functions/create_fiber_pop.function';
import { updateFiberPopSite } from './functions/update_fiber_pop_site.function';

const emptyForm = {
  fiber_pop: '',
  latest_status: '',
  ran_region: '',
  foo_region: '',
  city_name: '',
  type: '',
  telecom_region: '',
  ran_site_flag: '',
  ola_location_flag: '',
  site_name: '',
  technology: '',
  vendor: '',
  route: '',
  latt: '',
  long: '',
};

const toFormValue = (v) => (v == null || v === '--' ? '' : String(v));

export default function AddSinglePopFiberPopListComponent({ onPopAdded, onCancel, popToEdit }) {
  const { showBackdrop, hideBackdrop } = useBackdrop();
  const isEditMode = Boolean(popToEdit?.id);

  const [formData, setFormData] = useState(emptyForm);

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!popToEdit?.id) {
      return;
    }
    const row = popToEdit;
    setFormData({
      fiber_pop: toFormValue(row.fiber_pop),
      latest_status: toFormValue(row.latest_status),
      ran_region: toFormValue(row.ran_region),
      foo_region: toFormValue(row.foo_region),
      city_name: toFormValue(row.city_name),
      type: toFormValue(row.type),
      telecom_region: toFormValue(row.telecom_region),
      ran_site_flag: toFormValue(row.ran_site),
      ola_location_flag: toFormValue(row.ola_location),
      site_name: toFormValue(row.site_name),
      technology: toFormValue(row.technology),
      vendor: toFormValue(row.vendor),
      route: toFormValue(row.route),
      latt: row.latt != null && row.latt !== '' ? String(row.latt) : '',
      long: row.long != null && row.long !== '' ? String(row.long) : '',
    });
  }, [popToEdit]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fiber_pop.trim()) {
      newErrors.fiber_pop = 'Fiber POP is required';
    }
    if (!formData.latest_status.trim()) {
      newErrors.latest_status = 'Status is required';
    }
    if (!formData.ran_region.trim()) {
      newErrors.ran_region = 'RAN Region is required';
    }
    if (!formData.city_name.trim()) {
      newErrors.city_name = 'City is required';
    }
    if (formData.latt && isNaN(parseFloat(formData.latt))) {
      newErrors.latt = 'Latitude must be a valid number';
    }
    if (formData.long && isNaN(parseFloat(formData.long))) {
      newErrors.long = 'Longitude must be a valid number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      notify('error', 'Please fill in all required fields correctly');
      return;
    }

    try {
      showBackdrop();

      // Prepare payload matching the API structure exactly
      const payload = {
        fiber_pop: formData.fiber_pop,
        latest_status: formData.latest_status,
        ran_region: formData.ran_region,
        foo_region: formData.foo_region || null,
        city_name: formData.city_name,
        type: formData.type || null,
        telecom_region: formData.telecom_region || null,
        ran_site_flag: formData.ran_site_flag === '' ? null : formData.ran_site_flag || null,
        ola_location_flag:
          formData.ola_location_flag === '' ? null : formData.ola_location_flag || null,
        site_name: formData.site_name || null,
        technology: formData.technology || null,
        vendor: formData.vendor || null,
        route: formData.route || null,
        latitude: formData.latt || null,
        longitude: formData.long || null,
      };

      let response;
      if (isEditMode && popToEdit?.id) {
        response = await updateFiberPopSite(popToEdit.id, payload);
      } else {
        response = await createFiberPop(payload);
      }

      if (response?.status === 'success' || response?.response) {
        notify(
          'success',
          isEditMode ? 'Fiber POP updated successfully' : 'Fiber POP added successfully'
        );

        if (!isEditMode) {
          setFormData(emptyForm);
        }
        if (onPopAdded) {
          if (isEditMode && popToEdit?.id) {
            const toDisplay = (v) => (v == null || v === '' ? '--' : String(v));
            const lat = formData.latt ? parseFloat(formData.latt) : null;
            const long = formData.long ? parseFloat(formData.long) : null;
            onPopAdded({
              id: popToEdit.id,
              fiber_pop: toDisplay(formData.fiber_pop),
              latest_status: toDisplay(formData.latest_status),
              ran_region: toDisplay(formData.ran_region),
              foo_region: toDisplay(formData.foo_region),
              city_name: toDisplay(formData.city_name),
              type: toDisplay(formData.type),
              telecom_region: toDisplay(formData.telecom_region),
              ran_site: toDisplay(formData.ran_site_flag),
              ola_location: toDisplay(formData.ola_location_flag),
              site_name: toDisplay(formData.site_name),
              technology: toDisplay(formData.technology),
              vendor: toDisplay(formData.vendor),
              route: toDisplay(formData.route),
              latt: Number.isFinite(lat) ? lat : null,
              long: Number.isFinite(long) ? long : null,
            });
          } else {
            onPopAdded();
          }
        }
      }
    } catch (error) {
      console.error(isEditMode ? 'Error updating fiber POP:' : 'Error adding fiber POP:', error);
      notify(
        'error',
        error?.response?.data?.message ||
          (isEditMode ? 'Failed to update fiber POP' : 'Failed to add fiber POP')
      );
    } finally {
      hideBackdrop();
    }
  };

  const handleCancel = () => {
    setFormData(emptyForm);
    setErrors({});
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <div
            style={{
              padding: 8,
              background: '#f7f7f7',
              boxShadow: '0px 0px 30px 2px rgba(0, 0, 0, 0.05)',
              borderRadius: 8,
              height: 30,
              width: 30,
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            onClick={handleCancel}
          >
            <ArrowBackIosNewIcon style={{ fontSize: 18, color: '#1C274C' }} />
          </div>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
            {isEditMode ? 'Edit Fiber POP' : 'Add Single Fiber POP'}
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* Fiber POP */}
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                label="Fiber POP *"
                value={formData.fiber_pop}
                onChange={handleChange('fiber_pop')}
                error={!!errors.fiber_pop}
                helperText={errors.fiber_pop}
                required
              />
            </Grid>

            {/* Latest Status */}
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth required error={!!errors.latest_status}>
                <InputLabel>Status *</InputLabel>
                <Select
                  value={formData.latest_status}
                  onChange={handleChange('latest_status')}
                  label="Status *"
                >
                  <MenuItem value="Fiber Node">Fiber Node</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                </Select>
                {errors.latest_status && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {errors.latest_status}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* RAN Region */}
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth required error={!!errors.ran_region}>
                <InputLabel>RAN Region *</InputLabel>
                <Select
                  value={formData.ran_region}
                  onChange={handleChange('ran_region')}
                  label="RAN Region *"
                >
                  <MenuItem value="North">North</MenuItem>
                  <MenuItem value="Central A">Central A</MenuItem>
                  <MenuItem value="Central B">Central B</MenuItem>
                  <MenuItem value="South">South</MenuItem>
                </Select>
                {errors.ran_region && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {errors.ran_region}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* FOO Region */}
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                label="FOO Region"
                value={formData.foo_region}
                onChange={handleChange('foo_region')}
              />
            </Grid>

            {/* City Name */}
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                label="City *"
                value={formData.city_name}
                onChange={handleChange('city_name')}
                error={!!errors.city_name}
                helperText={errors.city_name}
                required
              />
            </Grid>

            {/* Type */}
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select value={formData.type} onChange={handleChange('type')} label="Type">
                  <MenuItem value="LDI">LDI</MenuItem>
                  <MenuItem value="LDO">LDO</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Telecom Region */}
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                label="Telecom Region"
                value={formData.telecom_region}
                onChange={handleChange('telecom_region')}
              />
            </Grid>

            {/* RAN Site Flag */}
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <InputLabel>RAN Site Flag</InputLabel>
                <Select
                  value={formData.ran_site_flag}
                  onChange={handleChange('ran_site_flag')}
                  label="RAN Site Flag"
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* OLA Location Flag */}
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <InputLabel>OLA Location Flag</InputLabel>
                <Select
                  value={formData.ola_location_flag}
                  onChange={handleChange('ola_location_flag')}
                  label="OLA Location Flag"
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Site Name */}
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                label="Site Name"
                value={formData.site_name}
                onChange={handleChange('site_name')}
              />
            </Grid>

            {/* Technology */}
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <InputLabel>Technology</InputLabel>
                <Select
                  value={formData.technology}
                  onChange={handleChange('technology')}
                  label="Technology"
                >
                  <MenuItem value="IPRAN">IPRAN</MenuItem>
                  <MenuItem value="GPON">GPON</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Vendor */}
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <InputLabel>Vendor</InputLabel>
                <Select value={formData.vendor} onChange={handleChange('vendor')} label="Vendor">
                  <MenuItem value="Huawei">Huawei</MenuItem>
                  <MenuItem value="ZTE">ZTE</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Route */}
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <InputLabel>Route</InputLabel>
                <Select value={formData.route} onChange={handleChange('route')} label="Route">
                  <MenuItem value="RING">RING</MenuItem>
                  <MenuItem value="STAR">STAR</MenuItem>
                  <MenuItem value="MESH">MESH</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Latitude */}
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                label="Latitude"
                type="number"
                value={formData.latt}
                onChange={handleChange('latt')}
                error={!!errors.latt}
                helperText={errors.latt || 'Enter latitude coordinate'}
                inputProps={{ step: 'any' }}
              />
            </Grid>

            {/* Longitude */}
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                label="Longitude"
                type="number"
                value={formData.long}
                onChange={handleChange('long')}
                error={!!errors.long}
                helperText={errors.long || 'Enter longitude coordinate'}
                inputProps={{ step: 'any' }}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <Button variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{ bgcolor: '#BD1D23', '&:hover': { bgcolor: '#8B1519' } }}
            >
              {isEditMode ? 'Update Fiber POP' : 'Add Fiber POP'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}
